// Плашка «вышло обновление» (09.08.2026).
//
// Открытая вкладка живёт на той сборке, с которой загрузилась: деплой не удаляет
// старые чанки, поэтому ничего не ломается — и ничего не сообщает. Человек
// смотрит на карточку, правка «уже на проде», а он её не видит. Плашка снимает
// ровно этот разрыв.
//
// Перезагружаем ТОЛЬКО по кнопке: молча дёрнуть reload — значит однажды стереть
// наполовину заполненную форму.
import { useEffect, useRef, useState } from 'react';

import { isStale, loadedMainChunk, reloadToFreshBuild, serverMainChunk } from '@/lib/stale-guard';

/** Как часто спрашиваем сервер. Раз в пять минут — незаметно и достаточно. */
const CHECK_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Сборка, о которой человек уже сказал «✕». Крестик раньше прятал плашку
 * только до следующей проверки (5 минут или возврат на вкладку), и посреди
 * переписки она возвращалась снова и снова — «не исчезает который день»
 * (жалоба 03.10.2026): перезагружаться с недописанным сообщением никто не
 * хочет. Теперь плашка молчит до СЛЕДУЮЩЕЙ выкладки. localStorage — чтобы
 * «✕» в одной вкладке не заставлял закрывать то же самое в соседней.
 */
const DISMISSED_KEY = 'update-banner-dismissed';

function dismissedBuild(): string | null {
  try {
    return localStorage.getItem(DISMISSED_KEY);
  } catch {
    return null;
  }
}

function rememberDismissed(build: string): void {
  try {
    localStorage.setItem(DISMISSED_KEY, build);
  } catch {
    // Хранилище недоступно (приватный режим) — плашка вернётся при проверке.
  }
}

export function UpdateBanner(): JSX.Element | null {
  const [outdated, setOutdated] = useState(false);
  // Серверная сборка, о которой сейчас говорит плашка: её и запоминает «✕».
  const latestRef = useRef<string | null>(null);
  // Снятие SW и чистка кэшей — асинхронные: до перезагрузки проходит заметная
  // доля секунды. Без отметки о начатом обновлении человек жмёт кнопку второй
  // раз, решив, что она не сработала (именно так это и выглядело).
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    // Отдельной ветки «не работать в dev» нет намеренно: в дев-сборке тега
    // `/assets/index-*.js` не существует, сверка честно ничего не находит и
    // молчит. Ветка, которую нельзя проверить тестом, — источник сюрпризов.
    // Сверку берём у сторожа сборки (`stale-guard`): он делает ровно ту же
    // проверку при старте вкладки, и две независимые копии регулярки с фетчем
    // однажды разошлись бы — одна звала бы обновляться, другая нет.
    const mine = loadedMainChunk();
    let alive = true;

    const check = async (): Promise<void> => {
      const latest = await serverMainChunk();
      if (!alive || !isStale(mine, latest) || latest === dismissedBuild()) return;
      latestRef.current = latest;
      setOutdated(true);
    };

    void check();
    const timer = setInterval(() => void check(), CHECK_INTERVAL_MS);
    // Возврат к вкладке — самый частый момент «а что там задеплоили»: проверяем
    // сразу, не дожидаясь следующего тика.
    const onFocus = (): void => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onFocus);

    return () => {
      alive = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, []);

  if (!outdated) return null;

  return (
    // Сверху, а не снизу: внизу экрана поле ввода переписки и кнопки форм —
    // плашка закрывала их (жалоба 03.10.2026).
    <div className="fixed left-1/2 top-3 z-50 -translate-x-1/2">
      <div className="flex items-center gap-3 rounded-full border border-divider-gray bg-ink-black px-4 py-2 text-sm text-canvas-white shadow-lg">
        <span>Вышло обновление CRM</span>
        <button
          type="button"
          disabled={updating}
          onClick={() => {
            setUpdating(true);
            void reloadToFreshBuild();
            // Кнопка не должна залипать навсегда: если перезагрузка не
            // случилась (замороженная вкладка, зависшее снятие SW), человек
            // должен иметь возможность нажать ещё раз — плашка «Обновляем…»
            // висела у сотрудника сутки (жалоба владельца 04.09.2026).
            setTimeout(() => setUpdating(false), 6000);
          }}
          className="rounded-full bg-canvas-white px-3 py-1 text-xs font-medium text-ink-black hover:opacity-90 disabled:opacity-70"
        >
          {updating ? 'Обновляем…' : 'Обновить'}
        </button>
        <button
          type="button"
          onClick={() => {
            if (latestRef.current) rememberDismissed(latestRef.current);
            setOutdated(false);
          }}
          aria-label="Скрыть сообщение об обновлении"
          className="text-canvas-white/60 hover:text-canvas-white"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
